/*
 * <rd-product-row> — product rows filled in the browser (see snippets/rd-product-row.liquid).
 *
 * data-mode="collection": best sellers of a collection (top 48), sold-out dropped,
 *                         shuffled, first N shown. Loads when scrolled near.
 * data-mode="recent":     products from this browser's recently-viewed list
 *                         (written by snippets/rd-recently-viewed-tracker.liquid).
 * data-mode="recommend":  Shopify recommendations for the last viewed product,
 *                         minus anything already in the recently-viewed list.
 *
 * Cards come from sections/rd-product-cards.liquid via the Section Rendering API,
 * so they are Dawn's own card-product markup.
 */
(() => {
  if (customElements.get('rd-product-row')) return;

  const RECENT_KEY = 'rd:recently-viewed';
  const CARDS_SECTION = 'rd-product-cards';

  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(list) ? list.filter((item) => item && item.handle) : [];
    } catch (e) {
      return [];
    }
  };

  const shuffle = (items) => {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  };

  const fetchCards = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-rd-cards] > li'));
  };

  const withSection = (url, params = {}) => {
    const u = new URL(url, window.location.origin);
    Object.entries({ ...params, section_id: CARDS_SECTION }).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  };

  // pre-order plants are sellable (at the sister site), so they count as available in rows
  const isAvailable = (li) => li.dataset.available === 'true' || li.dataset.preorder === 'true';

  class RdProductRow extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.limit = parseInt(this.dataset.limit, 10) || 8;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');

      if (this.dataset.mode === 'collection' && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '600px 0px' }
        );
        observer.observe(this);
      } else {
        this.load();
      }
    }

    // Filter chips (rd-product-grid): load a different collection into this row.
    reload(url) {
      this.dataset.url = url;
      this.setAttribute('aria-busy', 'true');
      this.load();
    }

    async load() {
      let items = [];
      try {
        if (this.dataset.mode === 'collection') items = await this.loadCollection();
        if (this.dataset.mode === 'recent') items = await this.loadRecent();
        if (this.dataset.mode === 'recommend') items = await this.loadRecommend();
        if (this.dataset.mode === 'related') items = await this.loadRelated();
      } catch (e) {
        console.error('[rd-product-row]', e);
      }
      this.render(items.slice(0, this.limit));
    }

    async loadCollection() {
      if (!this.dataset.url) return [];
      // data-sort (default best-selling) + data-shuffle="false" for a fixed order (e.g. lowest price first)
      const cards = await fetchCards(withSection(this.dataset.url, { sort_by: this.dataset.sort || 'best-selling' }));
      const inStock = cards.filter(isAvailable);
      return this.dataset.shuffle === 'false' ? inStock : shuffle(inStock);
    }

    // Product page "Pairs well with": complementary products first, related as fallback.
    async loadRelated() {
      const id = this.dataset.productId;
      if (!id) return [];
      const fetchIntent = (intent) =>
        fetchCards(withSection(`${this.root}recommendations/products`, { product_id: id, limit: 10, intent }));
      let cards = (await fetchIntent('complementary')).filter(isAvailable);
      if (cards.length < 2) cards = cards.concat((await fetchIntent('related')).filter(isAvailable));
      const seen = new Set([this.dataset.exclude]);
      return cards.filter((li) => !seen.has(li.dataset.handle) && seen.add(li.dataset.handle));
    }

    async loadRecent() {
      const handles = readRecent()
        .filter((item) => item.handle !== this.dataset.exclude)
        .slice(0, this.limit)
        .map((item) => item.handle);
      const results = await Promise.allSettled(
        handles.map((handle) => fetchCards(withSection(`${this.root}products/${encodeURIComponent(handle)}`)))
      );
      return results.filter((r) => r.status === 'fulfilled' && r.value[0]).map((r) => r.value[0]);
    }

    async loadRecommend() {
      const recent = readRecent();
      if (!recent.length || !recent[0].id) return [];
      const seen = new Set(recent.map((item) => item.handle));
      const cards = await fetchCards(
        withSection(`${this.root}recommendations/products`, {
          product_id: recent[0].id,
          limit: Math.min(this.limit + recent.length, 10),
          intent: 'related',
        })
      );
      return cards.filter((li) => isAvailable(li) && !seen.has(li.dataset.handle));
    }

    render(items) {
      const list = this.querySelector('[data-rd-list]');
      this.removeAttribute('aria-busy');

      if (!items.length) {
        const note = this.querySelector('[data-rd-note]');
        if (this.hasAttribute('data-design-mode') && note) {
          list.replaceChildren();
          note.hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }

      items.forEach((li) => this.scopeIds(li));
      list.replaceChildren(...items);
      this.hidden = false;

      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(list);
      this.setupArrows(list);
    }

    // Desktop arrows: page the row by its visible width; hidden when everything fits.
    setupArrows(list) {
      const arrows = this.querySelector('[data-rd-arrows]');
      if (!arrows) return;
      const prev = arrows.querySelector('[data-rd-prev]');
      const next = arrows.querySelector('[data-rd-next]');

      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        arrows.hidden = max <= 2;
        prev.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft >= max - 2;
      };
      const page = (direction) => list.scrollBy({ left: direction * list.clientWidth, behavior: 'smooth' });

      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      list.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
      update();
    }

    // card-product builds ids from the product id, so the same product in two rows
    // would duplicate ids; prefix them per row and update the references.
    scopeIds(root) {
      const prefix = `${this.id}-`;
      root.querySelectorAll('[id]').forEach((el) => (el.id = prefix + el.id));
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attr) => {
        root.querySelectorAll(`[${attr}]`).forEach((el) => {
          el.setAttribute(
            attr,
            el
              .getAttribute(attr)
              .split(/\s+/)
              .map((id) => (id ? prefix + id : id))
              .join(' ')
          );
        });
      });
    }
  }

  customElements.define('rd-product-row', RdProductRow);

  // Filter chips: <button data-rd-chip data-url> inside [data-rd-chips], target row in the same section.
  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-rd-chip]');
    if (!chip) return;
    const group = chip.closest('[data-rd-chips]');
    const row = chip.closest('.shopify-section')?.querySelector('rd-product-row');
    if (!group || !row || chip.getAttribute('aria-pressed') === 'true') return;
    group.querySelectorAll('[data-rd-chip]').forEach((b) => b.setAttribute('aria-pressed', String(b === chip)));
    row.reload(chip.dataset.url);
  });

  // "+" quick add on rd cards — same flow as Dawn's product-form (opens the cart drawer).
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-rd-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button');
    if (button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const formData = new FormData(form);
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(button);
    }

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (data.status) {
        button.classList.add('is-error');
        button.setAttribute('title', data.description || data.message || 'Could not add to cart');
        return;
      }
      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rd-quick-add', productVariantId: formData.get('id'), cartData: data });
      }
      cart.classList.remove('is-empty');
      cart.renderContents(data);
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
    } catch (error) {
      console.error('[rd-quick-add]', error);
      form.submit();
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });
})();
